import React, { useState, useEffect } from "react";
import { useTutorSessions } from "src/hooks/useTutorSessions";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/Table";
import { Input } from "src/components/ui/Input";
import { Button } from "src/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "src/components/ui/DropdownMenu";
import { Badge } from "src/components/ui/Badge";
import { Checkbox } from "src/components/ui/Checkbox";
import {
  Search,
  Filter,
  Columns,
  Download,
  AlertTriangle,
  FileText,
  Info,
  Calendar,
  BarChart3,
  MessageSquare,
  Pencil,
  RefreshCw,
  AlertCircle,
  Star,
  Clock,
  User,
  MoreHorizontal,
  Grid,
  List,
} from "lucide-react";
import LoadingSpinner from "src/components/LoadingSpinner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/components/ui/Tooltip";
import { Card, CardContent } from "src/components/ui/Card";
import TutorSessionCard, { getStudentDisplayName, getStudentPhoto, getStudentInitials } from "src/components/tutor/TutorSessionCard";
import { formatSessionTime, getTopicName, getSubtopicName, generateSessionId, getSubjectName } from "src/components/student/SessionCard";
import { usePageHeaderStore } from "src/store/pageHeaderStore";

// Interface for past session analytics data
interface SessionAnalytics {
  tutorTalkTime?: number;
  whiteboardInteractions?: number;
  messagesSent?: number;
  messagesReceived?: number;
  participationScore?: number;
  participationLevel?: string;
  notes?: string;
  feedbackRating?: number;
  actualDuration?: number;
}

// Past Sessions component
const PastSessions = () => {
  // Use real sessions hook for data fetching
  const {
    pastSessions,
    isLoadingPast,
    pastError,
    refreshPast
  } = useTutorSessions();

  // Local state for UI
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSessions, setSelectedSessions] = useState<string[]>([]);
  const [isCardView, setIsCardView] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    id: true,
    student: true,
    topic: true,
    subtopic: false,
    sessionType: true,
    date: true,
    duration: true,
    feedback: true,
    analytics: false,
    notes: false,
  });

  // Filter state
  const [filters, setFilters] = useState({
    sessionType: [] as string[],
    subject: [] as string[],
    rating: [] as string[],
  });

  // Filter sessions based on search term and filters
  const filteredSessions = pastSessions.filter((session) => {
    const searchString = searchTerm.toLowerCase();
    const studentName = getStudentDisplayName(session.student_profile).toLowerCase();
    const topicName = getTopicName(session.topic).toLowerCase();
    const sessionId = generateSessionId(session.id).toLowerCase();
    
    // Search filter
    const matchesSearch = !searchTerm || 
      sessionId.includes(searchString) ||
      studentName.includes(searchString) ||
      topicName.includes(searchString) ||
      (session.subtopic?.name?.toLowerCase().includes(searchString));

    // Session type filter
    const matchesSessionType = filters.sessionType.length === 0 || 
      filters.sessionType.includes(session.session_type);

    // Subject filter
    const matchesSubject = filters.subject.length === 0 || 
      filters.subject.includes(getSubjectName(session.topic));

    // Rating filter
    const sessionRating = getSessionRating(session);
    const matchesRating = filters.rating.length === 0 || 
      filters.rating.some(ratingRange => {
        if (ratingRange === '5') return sessionRating >= 4.5;
        if (ratingRange === '4') return sessionRating >= 3.5 && sessionRating < 4.5;
        if (ratingRange === '3') return sessionRating >= 2.5 && sessionRating < 3.5;
        if (ratingRange === 'low') return sessionRating < 2.5;
        if (ratingRange === 'none') return sessionRating === 0;
        return false;
      });

    return matchesSearch && matchesSessionType && matchesSubject && matchesRating;
  });

  // Calculate summary statistics
  const calculateStats = () => {
    if (pastSessions.length === 0) {
      return {
        totalSessions: 0,
        avgDuration: 0,
        avgRating: 0,
        totalHours: 0,
        completionRate: 0,
        topSubjects: []
      };
    }

    const totalSessions = pastSessions.length;
    const totalMinutes = pastSessions.reduce((sum, session) => sum + session.duration_min, 0);
    const avgDuration = Math.round(totalMinutes / totalSessions);
    const totalHours = Math.round((totalMinutes / 60) * 10) / 10;

    // Calculate average rating from feedback
    const ratingsData = pastSessions
      .filter(s => s.session_feedback && s.session_feedback.length > 0)
      .flatMap(s => s.session_feedback?.filter(f => f.rating) || []);
    
    const avgRating = ratingsData.length > 0 
      ? Math.round((ratingsData.reduce((sum, feedback) => sum + (feedback.rating || 0), 0) / ratingsData.length) * 10) / 10
      : 0;

    // Get top subjects
    const subjectCounts = pastSessions.reduce((acc, session) => {
      const subject = getSubjectName(session.topic);
      acc[subject] = (acc[subject] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topSubjects = Object.entries(subjectCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([subject]) => subject);

    return {
      totalSessions,
      avgDuration,
      avgRating,
      totalHours,
      completionRate: 100, // All past sessions are completed
      topSubjects
    };
  };

  const stats = calculateStats();

  // Get unique filter options
  const getFilterOptions = () => {
    const sessionTypes = [...new Set(pastSessions.map(s => s.session_type))];
    const subjects = [...new Set(pastSessions.map(s => getSubjectName(s.topic)))];
    
    return { sessionTypes, subjects };
  };

  const { sessionTypes, subjects } = getFilterOptions();

  // Helper function to get session rating
  const getSessionRating = (session: any): number => {
    if (!session.session_feedback || session.session_feedback.length === 0) return 0;
    const ratings = session.session_feedback.filter((f: any) => f.rating);
    if (ratings.length === 0) return 0;
    return ratings.reduce((sum: number, f: any) => sum + f.rating, 0) / ratings.length;
  };

  // Helper function to get participation badge
  const getParticipationBadge = (session: any) => {
    const rating = getSessionRating(session);
    if (rating >= 4.5) {
      return <Badge className="bg-green-100 text-green-800">⭐ Excellent</Badge>;
    } else if (rating >= 3.5) {
      return <Badge className="bg-blue-100 text-blue-800">Good</Badge>;
    } else if (rating >= 2.5) {
      return <Badge className="bg-yellow-100 text-yellow-800">Average</Badge>;
    } else if (rating > 0) {
      return <Badge className="bg-red-100 text-red-800">Needs Improvement</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-800">No Feedback</Badge>;
  };

  // Toggle filter function
  const toggleFilter = (filterType: keyof typeof filters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(item => item !== value)
        : [...prev[filterType], value]
    }));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      sessionType: [],
      subject: [],
      rating: [],
    });
  };

  // Check if filters are active
  const hasActiveFilters = () => {
    return filters.sessionType.length > 0 || filters.subject.length > 0 || filters.rating.length > 0;
  };

  // Action buttons for each session
  const getActionButtons = (session: any) => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem>
            <Download className="mr-2 h-4 w-4" />
            Export Session Data
          </DropdownMenuItem>
          <DropdownMenuItem>
            <FileText className="mr-2 h-4 w-4" />
            View Session Notes
          </DropdownMenuItem>
          <DropdownMenuItem>
            <AlertTriangle className="mr-2 h-4 w-4" />
            Report Issue
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const { setTitle, setDescription, reset } = usePageHeaderStore();
  useEffect(() => {
    setTitle("Past Sessions");
    setDescription("Review your completed sessions, engagement metrics, and teaching trends");
    return () => reset();
  }, [setTitle, setDescription, reset]);

  return (
    <>
      {/* Summary Analytics Panel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Session Stats</h3>
                <div className="mt-2 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-2xl font-bold">{stats.totalSessions}</p>
                    <p className="text-sm text-gray-500">Total Sessions</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.avgDuration} mins</p>
                    <p className="text-sm text-gray-500">Avg. Duration</p>
                  </div>
                </div>
              </div>
              <BarChart3 className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Performance</h3>
                <div className="mt-2 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-2xl font-bold">{stats.avgRating}/5</p>
                    <p className="text-sm text-gray-500">Avg. Rating</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.totalHours}h</p>
                    <p className="text-sm text-gray-500">Total Hours</p>
                  </div>
                </div>
              </div>
              <Star className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Top Subjects</h3>
                <div className="mt-2">
                  {stats.topSubjects.length > 0 ? (
                    <div className="space-y-1">
                      {stats.topSubjects.map((subject, index) => (
                        <div key={subject} className="flex items-center text-sm">
                          <span className="font-medium text-gray-700">#{index + 1}</span>
                          <span className="ml-2 text-gray-600">{subject}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No data available</p>
                  )}
                </div>
              </div>
              <Pencil className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Area */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
          <div className="relative flex-grow max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by student, subject, or topic"
              className="pl-10 pr-4 py-2 w-full border-gray-200 rounded-md focus:border-rfpurple-500 focus:ring focus:ring-rfpurple-200 focus:ring-opacity-50 transition-colors"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="flex items-center gap-2">
                  <Filter size={16} />
                  Filter
                  {hasActiveFilters() && (
                    <Badge variant="secondary" className="ml-1 h-4 w-4 p-0 text-xs">
                      {filters.sessionType.length + filters.subject.length + filters.rating.length}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <div className="p-2 text-sm font-medium text-gray-700 border-b">
                  Filter Options
                </div>

                {/* Session Type Filter */}
                <div className="p-2">
                  <div className="text-xs font-medium text-gray-600 mb-2">Session Type</div>
                  {sessionTypes.map(type => (
                    <DropdownMenuCheckboxItem
                      key={type}
                      checked={filters.sessionType.includes(type)}
                      onCheckedChange={() => toggleFilter('sessionType', type)}
                      className="capitalize"
                    >
                      {type}
                    </DropdownMenuCheckboxItem>
                  ))}
                </div>

                {/* Subject Filter */}
                <div className="p-2 border-t">
                  <div className="text-xs font-medium text-gray-600 mb-2">Subject</div>
                  {subjects.map(subject => (
                    <DropdownMenuCheckboxItem
                      key={subject}
                      checked={filters.subject.includes(subject)}
                      onCheckedChange={() => toggleFilter('subject', subject)}
                    >
                      {subject}
                    </DropdownMenuCheckboxItem>
                  ))}
                </div>

                {/* Rating Filter */}
                <div className="p-2 border-t">
                  <div className="text-xs font-medium text-gray-600 mb-2">Rating</div>
                  <DropdownMenuCheckboxItem
                    checked={filters.rating.includes('5')}
                    onCheckedChange={() => toggleFilter('rating', '5')}
                  >
                    ⭐ Excellent (4.5-5.0)
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={filters.rating.includes('4')}
                    onCheckedChange={() => toggleFilter('rating', '4')}
                  >
                    Good (3.5-4.4)
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={filters.rating.includes('3')}
                    onCheckedChange={() => toggleFilter('rating', '3')}
                  >
                    Average (2.5-3.4)
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={filters.rating.includes('low')}
                    onCheckedChange={() => toggleFilter('rating', 'low')}
                  >
                    Needs Improvement (&lt;2.5)
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={filters.rating.includes('none')}
                    onCheckedChange={() => toggleFilter('rating', 'none')}
                  >
                    No Feedback
                  </DropdownMenuCheckboxItem>
                </div>

                {hasActiveFilters() && (
                  <div className="p-2 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearAllFilters}
                      className="w-full"
                    >
                      Clear All Filters
                    </Button>
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="flex items-center gap-2">
                  <Columns size={16} />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="p-2 text-sm font-medium text-gray-700 border-b">
                  Toggle Columns
                </div>
                {Object.entries(visibleColumns).map(([key, value]) => (
                  <DropdownMenuCheckboxItem
                    key={key}
                    checked={value}
                    onCheckedChange={(checked) =>
                      setVisibleColumns({ ...visibleColumns, [key]: checked })
                    }
                    className="capitalize"
                  >
                    {key === "id" ? "Session ID" :
                     key === "sessionType" ? "Session Type" :
                     key === "date" ? "Date & Time" : key}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsCardView(!isCardView)}
              className="flex items-center gap-2"
            >
              {isCardView ? <List size={16} /> : <Grid size={16} />}
              {isCardView ? "Table View" : "Card View"}
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={refreshPast}
              className="flex items-center gap-2"
              disabled={isLoadingPast}
            >
              <RefreshCw size={16} className={isLoadingPast ? "animate-spin" : ""} />
              Refresh
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Download size={16} />
              Export
            </Button>
          </div>
        </div>

        {/* Content Area */}
        {isLoadingPast ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner />
            <span className="ml-2">Loading past sessions...</span>
          </div>
        ) : pastError ? (
          <div className="text-center py-6">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{pastError}</p>
            <Button onClick={refreshPast} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-2" />
            <p className="text-gray-500 mb-4">
              {pastSessions.length === 0 && !hasActiveFilters()
                ? "You haven't completed any sessions yet."
                : "No sessions match your search criteria."}
            </p>
            {pastSessions.length === 0 && !hasActiveFilters() && (
              <p className="text-sm text-gray-400">
                Your completed sessions will appear here once you finish teaching sessions.
              </p>
            )}
            {hasActiveFilters() && (
              <Button onClick={clearAllFilters} variant="outline" size="sm">
                Clear Filters
              </Button>
            )}
          </div>
        ) : isCardView ? (
          // Card View
          <div className="p-6 space-y-6">
            {filteredSessions.map((session) => (
              <TutorSessionCard
                key={session.id}
                session={session}
                showSessionId={visibleColumns.id}
                showActions={false} // Past sessions don't have actions
              />
            ))}
          </div>
        ) : (
          // Table View
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        selectedSessions.length === filteredSessions.length &&
                        filteredSessions.length > 0
                      }
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedSessions(filteredSessions.map((session) => session.id));
                        } else {
                          setSelectedSessions([]);
                        }
                      }}
                      aria-label="Select all"
                    />
                  </TableHead>
                  {visibleColumns.id && <TableHead>Session ID</TableHead>}
                  {visibleColumns.student && <TableHead>Student</TableHead>}
                  {visibleColumns.topic && <TableHead>Topic</TableHead>}
                  {visibleColumns.subtopic && <TableHead>Subtopic</TableHead>}
                  {visibleColumns.sessionType && <TableHead>Session Type</TableHead>}
                  {visibleColumns.date && <TableHead>Date & Time</TableHead>}
                  {visibleColumns.duration && <TableHead>Duration</TableHead>}
                  {visibleColumns.feedback && <TableHead>Feedback</TableHead>}
                  {visibleColumns.analytics && <TableHead>Analytics</TableHead>}
                  {visibleColumns.notes && <TableHead>Notes</TableHead>}
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSessions.map((session) => {
                  const { date, time } = formatSessionTime(session.scheduled_at);
                  const studentDisplayName = getStudentDisplayName(session.student_profile);
                  const studentPhoto = getStudentPhoto(session.student_profile);
                  const studentInitials = getStudentInitials(session.student_profile);
                  const topicName = getTopicName(session.topic);
                  const sessionId = generateSessionId(session.id);
                  const sessionRating = getSessionRating(session);

                  return (
                    <TableRow key={session.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedSessions.includes(session.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedSessions([...selectedSessions, session.id]);
                            } else {
                              setSelectedSessions(selectedSessions.filter(id => id !== session.id));
                            }
                          }}
                          aria-label={`Select session ${session.id}`}
                        />
                      </TableCell>
                      {visibleColumns.id && (
                        <TableCell className="font-medium">{sessionId}</TableCell>
                      )}
                      {visibleColumns.student && (
                        <TableCell>
                          <div className="flex items-center">
                            {studentPhoto ? (
                              <img
                                src={studentPhoto}
                                alt={studentDisplayName}
                                className="w-8 h-8 rounded-full object-cover mr-2"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  const initialsDiv = e.currentTarget.nextElementSibling as HTMLElement;
                                  if (initialsDiv) {
                                    initialsDiv.style.display = 'flex';
                                  }
                                }}
                              />
                            ) : null}
                            <div 
                              className={`w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-xs mr-2 ${studentPhoto ? 'hidden' : 'flex'}`}
                            >
                              {studentInitials}
                            </div>
                            <div>
                              <div className="font-medium">{studentDisplayName}</div>
                            </div>
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.topic && (
                        <TableCell>
                          <div>
                            <div className="font-medium">{topicName}</div>
                            {session.batch?.name && (
                              <p className="text-xs text-gray-500 mt-1">
                                {session.batch.name}
                              </p>
                            )}
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.subtopic && (
                        <TableCell>
                          {getSubtopicName(session.subtopic) || '—'}
                        </TableCell>
                      )}
                      {visibleColumns.sessionType && (
                        <TableCell>
                          <Badge variant="secondary" className="capitalize">
                            {session.session_type}
                          </Badge>
                        </TableCell>
                      )}
                      {visibleColumns.date && (
                        <TableCell>
                          <div>
                            <div className="font-medium">{date}</div>
                            <div className="text-sm text-gray-500">{time}</div>
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.duration && (
                        <TableCell>{session.duration_min} min</TableCell>
                      )}
                      {visibleColumns.feedback && (
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {sessionRating > 0 ? (
                              <>
                                <div className="flex items-center">
                                  <Star className="h-4 w-4 text-yellow-400 fill-current" />
                                  <span className="ml-1 text-sm font-medium">{sessionRating.toFixed(1)}</span>
                                </div>
                                {getParticipationBadge(session)}
                              </>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-800">No Feedback</Badge>
                            )}
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.analytics && (
                        <TableCell>
                          <div className="text-sm space-y-1">
                            {/* This would show session analytics if available in session_details */}
                            <div>Analytics available</div>
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.notes && (
                        <TableCell>
                          {/* This would show session notes if available */}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <FileText className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">Session notes available</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                      )}
                      <TableCell>{getActionButtons(session)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </>
  );
};

export default PastSessions;